<template>
  <div class="flex flex-col h-full conversations">
    <app-header class="z-10 pl-4">
      <filter-menu
        :show-request-outcomes="true"
        :show-assistance-resolutions="true"
        :show-answer="true"
        :show-channels="true"
        :show-markets="true"
        :use-logical-bots="true"
        :is-loading="loaders.filters"
        @filter-change="onFilterChanged"
      />
    </app-header>

    <empty-view v-if="!hasConversations && !initialLoading"
                text="No conversations match your selected filters."
                :is-loading="loaders.conversations"
                image-name="noConversationsImage"
                class="h-full justify-center items-center test-empty-view-conversations"/>

    <div v-else class="w-full h-full flex overflow-auto">
      <conversations-list ref="conversationList"
                          :bot-ids="selectedBotIds"
                          :selected-conversation-bot-id="conversationBotId"
                          :conversations="conversations"
                          :has-next="hasNext"
                          :is-loading="loaders.conversations"
                          :contact-id="contactId"
                          :was-navigation-clicked="wasNavigationClicked()"
                          @select-conversation="selectConversation"
                          @load-conversations="loadMoreConversations(true)"
                          class="w-3/12 max-h-full"
      />
      <shadowed-wrapper class="w-5/12 flex flex-col h-full">
        <template>
          <conversation-detail :contact-id="contactId"
                               :selected-bubble="selectedBubble"
                               ref="conversationDetail"
                               class="h-full"
                               :is-loading.sync="loaders.conversationDetail"
                               :answers-by-intent-id="answersByIntentId"
                               :answers-by-id="answersById"
                               @client-message-selected="onClientMessageSelected"
                               @remote-action-selected="onRemoteActionSelected"
                               @chat-gpt-action-selected="onChatGPTActionSelected"
                               @event-selected="onEventSelected"
                               @clear-bubble-selected="onClearBubble"
          />
          <conversation-navigation ref="conversationNavigation"
                                   class="h-16"
                                   :contact-id="contactId"
                                   :is-conversation-loading="loaders.conversationDetail"
                                   :conversations="conversations"
                                   @bubble-selected="onClientMessageSelected"
                                   @select-conversation="selectConversation"
          />
        </template>
      </shadowed-wrapper>

      <transition name="slide-fade" mode="out-in">
        <EmptyView
          v-if="showNoRequestsState"
          text="There are no requests in the selected conversation."
          class="w-4/12 my-auto"
          image-name="noConversationsImage"
          key="no-request"
        />
        <ChatGPTActionDetail v-else-if="formData.type === 'chat_gpt_action'"
          class="w-4/12"
          :form-data="formData"
          :key="`chat-gpt-${formData.id}`"
        />
        <EventDetail
          v-else-if="formData.type === 'event' || formData.type === 'bot_wake_up'"
          class="w-4/12"
          :form-data="formData"
          :bot-id="conversationScopedBotId"
          :key="`event-${formData.id}`"
        />
        <ApiCallDetail v-else-if="formData.type === 'remote_action'"
          class="w-4/12"
          :request-data="selectedBubble.requestData"
          :response-data="selectedBubble.responseData"
          :form-data="formData"
          :key="`api-${formData.id}`"
        />
        <CustomerRequestDetail
          v-else
          class="w-4/12"
          :form-data="formData"
          :bot-id="conversationScopedBotId"
          :answers-by-intent-id="answersByIntentId"
          :is-loading="loaders.conversationDetail"
          :key="`request-${formData.messageId}`"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import analyticsFilterModule from '@/store/modules/analytics-filter.module'
import conversationDetailModule from '@/store/modules/conversation-detail.module'
import AppHeader from '@/components/common/AppHeader.vue'
import ConversationsList from './ConversationsList.vue'
import FilterMenu from '../filter/FilterMenu.vue'
import { FILTER_ITEMS, IGNORED_OUTCOMES } from '@/utils/filter.constants'
import clone from 'ramda/src/clone'
import isEmpty from 'ramda/src/isEmpty'
import analyticsService from '@/services/amio/analytics.service'
import ConversationDetail from './detail/ConversationDetail.vue'
import EmptyView from '@/components/editor/EmptyView.vue'
import toaster from '@/utils/toaster'
import botModule from '@/store/modules/bot.module'
import ShadowedWrapper from '@/components/base-components/ShadowedWrapper.vue'
import { ROUTE_NAMES } from '@/router'
import ConversationNavigation from '@/components/analytics/conversations/detail/ConversationNavigation.vue'
import answerModule from '@/store/modules/answer.module'
import answerService from '@/services/amio/answer.service'
import channelModule from '@/store/modules/channel.module'
import marketModule from '@/store/modules/market.module'
import marketService from '@/services/amio/market.service'
import { getBotIdsForLogicalBots, getLogicalBotId, resolveBotRouteId } from '@/utils/market.utils'
import errorUtils from '@/utils/error.utils'
import variablesModule from '@/store/modules/variables.module'
import ApiCallDetail from '@/components/analytics/conversations/detail/ApiCallDetail.vue'
import ChatGPTActionDetail from '@/components/analytics/conversations/detail/ChatGPTActionDetail.vue'
import CustomerRequestDetail from '@/components/analytics/conversations/detail/CustomerRequestDetail.vue'
import EventDetail from '@/components/analytics/conversations/detail/EventDetail.vue'

export default {
  name: 'Conversations',

  metaInfo: {
    title: 'History'
  },

  components: {
    ApiCallDetail,
    ChatGPTActionDetail,
    CustomerRequestDetail,
    EventDetail,
    ShadowedWrapper,
    AppHeader,
    ConversationsList,
    FilterMenu,
    ConversationDetail,
    EmptyView,
    ConversationNavigation
  },

  props: {
    botId: {
      type: String,
      required: false,
      default: null
    }
  },

  data() {
    return {
      initialLoading: true,
      conversations: [],
      cursor: {},
      cursorsByBotId: {},
      formData: {},
      selectedBubble: null,
      contactId: this.$route.query.contactId,
      loaders: {
        conversations: false,
        filters: true,
        conversationDetail: false
      }
    }
  },

  computed: {
    dateFrom: analyticsFilterModule.getDateFrom,
    dateTo: analyticsFilterModule.getDateTo,
    requestOutcomes: analyticsFilterModule.getRequestOutcomes,
    assistanceResolutions: analyticsFilterModule.getAssistanceResolutions,
    bot: analyticsFilterModule.getBot,
    logicalBots: analyticsFilterModule.getLogicalBots,
    markets: analyticsFilterModule.getMarkets,
    answerId: analyticsFilterModule.getAnswerId,
    answerList: answerModule.getAnswerList,
    channels: analyticsFilterModule.getChannels,
    requests: conversationDetailModule.getRequests,

    // A conversation (one contact) is always answered by a single bot — resolve it from any
    // of its own requests, instead of the page-level filter bot, which can differ once History
    // is showing conversations aggregated across multiple bots.
    conversationBotId() {
      return Object.values(this.requests).find(({ bot_id }) => bot_id)?.bot_id || null
    },

    conversationScopedBotId() {
      return this.conversationBotId || this.resolvedBotId
    },

    // logicalBots === null means "the anchor bot only" (default)
    selectedLogicalBotIds() {
      if (this.logicalBots !== null && this.logicalBots !== undefined) return this.logicalBots

      const anchorLogicalBotId = getLogicalBotId(this.bot)
      return anchorLogicalBotId ? [anchorLogicalBotId] : []
    },

    selectedBotIds() {
      return getBotIdsForLogicalBots(this.selectedLogicalBotIds, this.markets)
    },

    hasConversations() {
      return !isEmpty(this.conversations)
    },

    hasNext() {
      return isEmpty(this.cursor) ? false : this.cursor.has_next
    },

    hasRequest() {
      return !isEmpty(this.formData)
    },

    showNoRequestsState() {
      return !this.loaders.conversationDetail && !this.hasRequest
    },

    isContactIdLoaded() {
      return this.conversations.some(({ contact_id }) => contact_id === this.$route.query.contactId)
    },

    answersByIntentId() {
      const pairs = this.answerList.map(({ intent, name, id }) => {
        return [intent.platformId, { name, id, intentId: intent.id }]
      })
      return Object.fromEntries(pairs)
    },

    answersById() {
      const pairs = this.answerList.map(({ name, id }) => [id, { name, id }])
      return Object.fromEntries(pairs)
    },

    resolvedBotId() {
      return this.bot?.id || this.botId || botModule.getBotId()
    }
  },

  methods: {
    getRouteLogicalBotIds() {
      const { bots } = this.$route.query
      if (typeof bots === 'undefined') return null
      if (bots === '') return []
      return bots ? bots.split(',').filter(Boolean) : []
    },

    // Accepts either a real variant bot id or a logical (multi_bot_id or standalone) id.
    resolvePrimaryBotId(botIdOrLogicalId) {
      const botList = botModule.getBotList()
      if (!botIdOrLogicalId) return botModule.getBotId()

      const isStandaloneLogical = botList.some(({ id, multi_bot_id }) => id === botIdOrLogicalId && !multi_bot_id)
      if (isStandaloneLogical) return botIdOrLogicalId

      const isMultiGroupLogical = botList.some(({ multi_bot_id }) => multi_bot_id === botIdOrLogicalId)
      if (isMultiGroupLogical) return resolveBotRouteId(botIdOrLogicalId) || botIdOrLogicalId

      return botModule.resolvePrimaryBot(botIdOrLogicalId)?.id || botIdOrLogicalId
    },

    wasNavigationClicked() {
      const { stepForwardWasClicked, stepBackWasClicked } = this.$refs.conversationNavigation || {}
      return stepForwardWasClicked || stepBackWasClicked
    },

    async loadInitialConversations() {
      this.cursorsByBotId = {}
      this.cursor = {}
      this.setLoader('conversations', true)
      this.setConversations(await this.loadConversations(), true)

      if (!isEmpty(this.conversations)) {
        const isConversationExisting = await this.isConversationsExisting(this.$route.query.contactId)
        while (!this.isContactIdLoaded && isConversationExisting) {
          if (this.hasNext) {
            await this.loadMoreConversations(false)
            continue
          }
          break
        }

        const contactId = this.isContactIdLoaded ? this.contactId : this.conversations[0].contact_id
        this.selectConversation(contactId, true)
      }

      this.setLoader('conversations', false)
    },

    async loadConversations() {
      if (!this.selectedBotIds.length) {
        this.cursor = {}
        this.cursorsByBotId = {}
        return []
      }

      try {
        const pages = await Promise.all(this.selectedBotIds.map(botId => this.loadConversationsForBot(botId)))
        const conversations = []
        const cursorsByBotId = { ...this.cursorsByBotId }
        let hasNext = false

        pages.forEach(({ botId, conversations: list, cursor }) => {
          if (cursor) cursorsByBotId[botId] = cursor
          if (cursor?.has_next) hasNext = true
          conversations.push(...list.map(conversation => ({ ...conversation, bot_id: botId })))
        })

        this.cursorsByBotId = cursorsByBotId
        this.cursor = { has_next: hasNext }
        return conversations.sort((a, b) => {
          return new Date(b.last_request_timestamp) - new Date(a.last_request_timestamp)
        })
      } catch (err) {
        const errorDescription = errorUtils.resolveMessage(err)
        toaster.error(errorDescription, err)
      }
    },

    async loadConversationsForBot(botId) {
      const cursor = this.cursorsByBotId[botId] || {}
      if (cursor.has_next === false) {
        return { botId, conversations: [], cursor }
      }

      const [dataAutomations, dataResolutions] = await Promise.all(this.resolveConversationRequests(botId, cursor))
      return {
        botId,
        conversations: this.getConversations(dataAutomations.conversations, dataResolutions?.conversations) || [],
        cursor: dataAutomations.cursor || {}
      }
    },

    async loadMoreConversations(isUserScrolling) {
      if (this.loaders.conversations && isUserScrolling) return

      this.setLoader('conversations', true)
      this.setConversations(await this.loadConversations())
      this.setLoader('conversations', false)
    },

    getConversations(conversationsAutomation, conversationsResolution) {
      if (!conversationsResolution) return conversationsAutomation

      const resolutionContactIds = new Set(conversationsResolution.map(({ contact_id }) => contact_id))

      return conversationsAutomation.filter(({ contact_id }) => {
        return resolutionContactIds.has(contact_id)
      })
    },

    setConversations(conversations, resetConversations = false) {
      if (resetConversations) {
        this.conversations = conversations
        return
      }

      this.conversations.push(...conversations)
    },

    resolveConversationRequests(botId = this.selectedBotIds, cursor = this.cursor) {
      const payloadOutcomes = {
        botId,
        from: this.dateFrom,
        to: this.dateTo,
        answerId: this.answerId,
        cursor,
        ignoreOutcomes: IGNORED_OUTCOMES,
        resolutions: null,
        outcomes: this.requestOutcomes,
        channels: this.channels
      }
      const requests = [analyticsService.getConversations(payloadOutcomes)]

      if (this.assistanceResolutions) {
        const payloadResolutions = {
          ...payloadOutcomes,
          outcomes: null,
          resolutions: this.assistanceResolutions,
          ignoreOutcomes: null
        }
        requests.push(analyticsService.getConversations(payloadResolutions))
      }

      return requests
    },

    async isConversationsExisting(contactId) {
      if (!contactId) return false

      try {
        const response = await analyticsService.getConversationHistory(contactId)
        return !isEmpty(response.history_events)
      } catch(err) {
        const errorDescription = errorUtils.resolveMessage(err)
        toaster.error(errorDescription, err)
        // bootstrap conversations has no request and return error (500)
        return true
      }
    },

    async loadAnswers(botId) {
      answerModule.setAnswerList(await answerService.list(botId))
    },

    onClientMessageSelected(formData) {
      this.onClearBubble()
      this.formData = formData
      if (isEmpty(formData)) return

      this.selectedBubble = {
        messageId: formData.messageId
      }

      if (formData.goToTargetMessage) {
        this.$refs.conversationDetail.handleScrollToBubble({ id: formData.messageId })
      }

      this.updateRouteQueryRequestId(formData.messageId)
    },

    updateRouteQueryRequestId(requestId) {
      if (this.$route.query.requestId === requestId) return

      this.$router.push(this.getMessageRoute(requestId))
    },

    getMessageRoute(requestId) {
      return {
        name: ROUTE_NAMES.history,
        query: { ...this.$route.query, requestId, messageId: undefined }
      }
    },

    async onFilterChanged(filterValues) {
      this.setLoader('conversations', true)
      this.setLoader('conversationDetail', true)
      analyticsFilterModule.setLogicalBots(filterValues[FILTER_ITEMS.BOTS])
      analyticsFilterModule.setMarkets(filterValues[FILTER_ITEMS.MARKETS])
      analyticsFilterModule.setDateFrom(filterValues[FILTER_ITEMS.DATE_FROM])
      analyticsFilterModule.setDateTo(filterValues[FILTER_ITEMS.DATE_TO])
      analyticsFilterModule.setRequestOutcomes(filterValues[FILTER_ITEMS.REQUEST_OUTCOME])
      analyticsFilterModule.setAssistanceResolutions(filterValues[FILTER_ITEMS.ASSISTANCE_RESOLUTIONS])
      analyticsFilterModule.setAnswerId(filterValues[FILTER_ITEMS.ANSWER_ID])
      analyticsFilterModule.setChannels(filterValues[FILTER_ITEMS.CHANNELS])
      await channelModule.setChannelListForBots(this.selectedBotIds.length ? this.selectedBotIds : [this.resolvedBotId])

      if (this.$refs.conversationList) {
        this.$refs.conversationList.wasScrolled = false
      }

      this.cursor = {}
      this.cursorsByBotId = {}
      // to reset contactId
      this.selectConversation(null, true)
      await this.loadInitialConversations()
    },

    selectConversation(contactId = null, calledFromMethod = false) {
      if (!calledFromMethod) this.onClearBubble()
      this.contactId = contactId
      const conversationDetailRef = this.$refs.conversationDetail
      if (contactId && conversationDetailRef) {
        conversationDetailRef.$el.scrollTop = 0
      }

      if (contactId === this.$route.query.contactId) return

      this.$router.replace({ query: { ...this.$route.query, contactId } })
    },

    async initFilters(botIdOrLogicalId = this.resolvedBotId) {
      try {
        // Bot list must be loaded before resolvePrimaryBotId — logical multi-bot ids
        // (e.g. from /history?bots=...) can only be mapped to a real variant once the list exists.
        await botModule.setBotList()
        const primaryBotId = this.resolvePrimaryBotId(botIdOrLogicalId)
        const [, markets] = await Promise.all([
          analyticsFilterModule.setBot(primaryBotId),
          marketService.list()
        ])
        marketModule.setMarketList(markets)
        botModule.setBot(this.bot)
        await channelModule.setChannelListForBots(this.selectedBotIds.length ? this.selectedBotIds : [primaryBotId])
      } catch (err) {
        toaster.error(err)
      }
      this.setLoader('filters', false)
    },

    setLoader(loader, isLoading) {
      this.$set(this.loaders, loader, isLoading)
    },

    onRemoteActionSelected(formData) {
      this.formData = formData
      this.selectedBubble = {
        messageId: this.formData.id,
        requestData: this.formData.data.remoteAction,
        responseData: this.formData.data.remoteResponse || this.formData.data.context.apiCall
      }
    },

    onChatGPTActionSelected(formData) {
      const resolvedFormData = clone(formData)
      // PRO-517: a GPT turn that failed is stored with no action at all, so reading its prompt threw here and
      // the right panel never opened. ChatGPTActionDetail renders what such an event does have.
      if (formData.data.action?.prompt?.startsWith('1. If the user is asking for product/service recommendations')) {
        if (!this.showUnreleased) resolvedFormData.data.action.prompt = '**AMIO PROMPT HIDDEN**'
        resolvedFormData.data.action.isFallback = true
      }
      this.formData = resolvedFormData
      this.selectedBubble = {
        messageId: this.formData.id
      }
    },

    onEventSelected(formData) {
      this.formData = formData
      this.selectedBubble = {
        messageId: this.formData.id
      }
    },

    onClearBubble() {
      this.formData = {}
      this.selectedBubble = null
    }
  },

  watch: {
    conversationBotId(botId) {
      if (botId) this.loadAnswers(botId)
    }
  },

  async mounted() {
    this.setLoader('conversations', true)
    this.setLoader('conversationDetail', true)

    const routeLogicalBotIds = this.getRouteLogicalBotIds()
    if (routeLogicalBotIds === null) {
      await this.initFilters(this.botId || botModule.getBotId())
    } else {
      const anchorLogicalBotId = routeLogicalBotIds[0] || botModule.getBotId()
      await this.initFilters(anchorLogicalBotId)
    }

    await variablesModule.fetchVariables()
    await this.loadInitialConversations()
    this.initialLoading = false
  }
}
</script>

<style lang="scss">
.conversations {
  .slide-fade-enter-active {
    transition: all .3s ease;
  }

  .slide-fade-leave-active {
    transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }

  .slide-fade-enter, .slide-fade-leave-to {
    transform: translateX(-10px);
    opacity: 0;
  }
}
</style>
